import React, { useEffect, useState } from 'react';
import { Form } from '@dashboard/components/Formik';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import Button from '@dashboard/components/Button';
import { stringify } from 'query-string';
import { fetcher } from '@dashboard/services/request';
import { useMutation, useQuery } from '@tanstack/react-query';
import { appUrl } from '@dashboard/services/env';
import { sellerGoogleKey } from '@dashboard/lib/queryKeys';
import { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import { CheckIcon } from '@heroicons/react/24/outline';
import Link from '@dashboard/components/Link';

export default function GoogleSheetsForm({ integration, onSubmit }) {
  const { token, sellerId } = useAuth();
  const [newAccount, setNewAccount] = useState(null);
  const [error, setError] = useState(null);

  const { data: existingAccount, isLoading } = useQuery({
    queryKey: sellerGoogleKey(sellerId),
    queryFn: () => fetcher(`/oauth/google?sellerId=${sellerId}`, token),
    enabled: !!sellerId,
    retry: false,
  });

  if (isLoading) {
    return null;
  }

  // select existing account or add new account
  if (!existingAccount && !newAccount) {
    return (
      <GoogleSheetsAuthFlow
        onDone={account => setNewAccount(account)}
        error={error}
        setError={setError}
      />
    );
  }

  // if integration already exists, don't show the form
  if (integration?._id) {
    return (
      <div className="space-y-2">
        <div className="text-sm text-gray-600">
          {existingAccount.config?.email ? (
            <div className="flex gap-x-1">
              <CheckIcon className="inline-block h-5 w-5 text-green-600" />
              <div>
                Connected to: <span className="font-medium">{existingAccount.config?.email}</span>
              </div>
            </div>
          ) : (
            <div className="flex gap-x-1">
              <CheckIcon className="inline-block h-5 w-5 text-green-600" />
              <div>Connected to Google</div>
            </div>
          )}
        </div>

        <div className="text-sm text-gray-600">
          The connected Google account needs to have write access to{' '}
          <Link
            href={`https://docs.google.com/spreadsheets/d/${integration.config.spreadsheetId}`}
            target="_blank"
            variant="secondary"
          >
            the sheet
          </Link>
          .
        </div>

        <Button appearance="secondary" size="small" href="/settings/integrations">
          Change Google account
        </Button>
      </div>
    );
  }

  const account = existingAccount || newAccount;

  // create or add sheet
  return (
    <Form
      initialValues={{
        config: {
          exportExistingData: integration?.config?.exportExistingData || false,
          oauthId: account._id,
        },
      }}
      onSubmit={onSubmit}
      submitButton={{
        label: 'Create sheet',
      }}
    />
  );
}

function GoogleSheetsAuthFlow({ onDone, error, setError }) {
  const { token, sellerId } = useAuth();

  // creates oauth record from code
  const createOAuthMutation = useMutation({
    mutationFn: code => {
      return fetcher(`/oauth/google`, token, {
        method: 'POST',
        body: JSON.stringify({
          code,
          sellerId: sellerId,
          redirectURI: `${appUrl}/dashboard/integrations/google/callback`,
        }),
      });
    },
    onSuccess: data => {
      onDone(data);
    },
    onError: error => {
      setError(error.message);
    },
  });

  function connectNewAccount() {
    const endpointUrl = `https://accounts.google.com/o/oauth2/v2/auth?${stringify({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: `${appUrl}/dashboard/integrations/google/callback`,
      access_type: 'offline',
      response_type: 'code',
      prompt: 'consent',
      scope: [
        'https://www.googleapis.com/auth/spreadsheets',
        'https://www.googleapis.com/auth/userinfo.email',
      ].join(' '),
    })}`;

    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2.5;

    window.open(
      endpointUrl,
      'popup',
      `popup=true,width=${width},height=${height},left=${left},top=${top}`,
    );
  }

  useEffect(() => {
    const messageHandler = event => {
      if (event.origin !== appUrl) return;

      const { type, code } = event.data;

      if (type === 'google-sheets-oauth-callback' && code) {
        createOAuthMutation.mutate(code);
      }
    };

    window.addEventListener('message', messageHandler);

    return () => window.removeEventListener('message', messageHandler);
  }, []);

  return (
    <>
      <Button onClick={connectNewAccount} appearance="primary">
        Connect with Google Sheets
      </Button>

      {error && (
        <div className="mt-4">
          <FormErrorBase>{error}</FormErrorBase>
        </div>
      )}
    </>
  );
}
